import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { useNavigate } from 'react-router';
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import ImagePicker from '../components/ImagePicker';
import { RichTextEditor } from '../components/RichTextEditor';

const API_URL = import.meta.env.VITE_API_URL;

export default function BroadcastAdd() {
  const navigate = useNavigate();

  const [broadcast, setBroadcast] = React.useState<any>({
    message: '',
    cityId: null,
    scheduledDateAt: dayjs().format('DD.MM.YYYY'),
    scheduledTimeAt: dayjs().format('HH:mm'),
    channelType: 'all',
    auditory: 'all',
  });

  const [cities, setCities] = React.useState<any[]>([]);
  const [image, setImage] = React.useState<File | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  const [scheduledDate, setScheduledDate] = React.useState<Dayjs | null>(
    dayjs(),
  );
  const [scheduledTime, setScheduledTime] = React.useState<Dayjs | null>(
    dayjs(),
  );

  const [errorMessage, setErrorMessage] = React.useState<string>('');

  React.useEffect(() => {
    fetch(`${API_URL}/cities`)
      .then((r) => r.json())
      .then((r) => setCities(r.cities));
  }, []);

  const save = () => {
    if (!broadcast.message || broadcast.message.trim() === '') {
      setErrorMessage('Повідомлення не може бути пустим');
      return;
    }

    const formData = new FormData();

    formData.append('message', broadcast.message);
    formData.append('cityId', broadcast.cityId ?? '');
    formData.append('scheduledDateAt', broadcast.scheduledDateAt ?? '');
    formData.append('scheduledTimeAt', broadcast.scheduledTimeAt ?? '');
    formData.append('channelType', broadcast.channelType ?? 'all');
    formData.append('auditory', broadcast.auditory ?? 'all');

    if (image) {
      formData.append('image', image);
    } else if (mediaId !== null) {
      formData.append('mediaId', String(mediaId));
    }

    fetch(`${API_URL}/broadcast`, {
      method: 'POST',
      body: formData,
    }).then(() => navigate('/broadcasts'));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: 700 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Додати нову розсилку
        </Typography>

        <RichTextEditor
          value={broadcast.message}
          onChange={(html) => setBroadcast({ ...broadcast, message: html })}
          label="Повідомлення"
          minRows={4}
        />

        {errorMessage && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Місто</InputLabel>
          <Select
            value={broadcast.cityId ?? ''}
            onChange={(e) =>
              setBroadcast({
                ...broadcast,
                cityId: e.target.value || null,
              })
            }
          >
            <MenuItem value="">Усі міста</MenuItem>
            {cities.map((c) => (
              <MenuItem key={c.citiesId} value={c.citiesId}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Канал</InputLabel>
          <Select
            value={broadcast.channelType ?? 'all'}
            onChange={(e) =>
              setBroadcast({ ...broadcast, channelType: e.target.value })
            }
          >
            <MenuItem value="viber">Viber</MenuItem>
            <MenuItem value="telegram">Telegram</MenuItem>
            <MenuItem value="all">Всі</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Аудиторія</InputLabel>
          <Select
            value={broadcast.auditory ?? 'all'}
            onChange={(e) =>
              setBroadcast({ ...broadcast, auditory: e.target.value })
            }
          >
            <MenuItem value="new">Нова</MenuItem>
            <MenuItem value="bot">Аудиторія бота</MenuItem>
            <MenuItem value="all">Всі</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DatePicker
            label="Дата відправки"
            value={scheduledDate}
            format="DD.MM.YYYY"
            onChange={(v) => {
              setScheduledDate(v);
              setBroadcast({
                ...broadcast,
                scheduledDateAt: v
                  ? v.format('DD.MM.YYYY')
                  : dayjs().format('DD.MM.YYYY'),
              });
            }}
          />

          <TimePicker
            label="Час відправки"
            value={scheduledTime}
            onChange={(v) => {
              setScheduledTime(v);
              setBroadcast({
                ...broadcast,
                scheduledTimeAt: v
                  ? v.format('HH:mm')
                  : dayjs().format('HH:mm'),
              });
            }}
            ampm={false}
          />
        </Box>

        <ImagePicker
          title="Зображення"
          value={imageUrl || ''}
          onChange={(value) => {
            if (value.type === 'file') {
              setImage(value.file);
              setMediaId(null);
              setImageUrl(URL.createObjectURL(value.file));
            }

            if (value.type === 'media') {
              setImage(null);
              setMediaId(value.mediaId);
              setImageUrl(value.url);
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={save}>
            Створити
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Скасувати
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
